import { useState, useEffect } from "react";
import { Search, Copy, Calendar, Building2, Truck, User, Mail, Phone, MapPin, FileText } from "lucide-react";
import axios from "axios";
import Swal from 'sweetalert2';

import API_BASE_URL from "../config";

export default function PurchaseOrder() {
    const [formData, setFormData] = useState({
        // Header Info
        poNumber: "",
        poCreatedDate: new Date().toISOString().split('T')[0],
        referencePRId: 0,
        inventoryDestinationPRId: 0,
        paymentMode: "",
        tin: "",

        // Charging Company
        companyId: "",
        company_Address1: "",
        company_Address2: "",
        company_TIN: "",
        company_ContactNumber: "",
        company_Email: "",
        departmentId: "",
        locationId: "",

        // Vendor Information
        vendorId: "",
        vendor_Address1: "",
        vendor_Address2: "",
        vendor_ContactPerson: "",
        vendor_ContactNumber: "",
        terms: "",

        // Ship To Details
        shipTo_Location: "",
        shipTo_Address1: "",
        shipTo_Address2: "",
        shipTo_ContactPerson: "",
        shipTo_ContactNumber: "",
        deliveryDate: "",

        // Other fields
        remarks: "",
        isVatInclusive: true,
        headerDiscount: 0,
        preparedBy: 0,
        status: 0,
        isPOPrinted: false,
        reprintCount: 0,
        receivingStatus: "Pending",
        isFromQuotation: 0,
        isNonVAT: false
    });
    const [approvalUsers, setApprovalUsers] = useState([
        { id: 1, name: "Juan Dela Cruz", role: "Manager" },
        { id: 2, name: "Maria Santos", role: "Supervisor" },
        { id: 3, name: "Mark Reyes", role: "Accounting" },
    ]);

    const [selectedPRItems, setSelectedPRItems] = useState([]);
    const [prDetailsModal, setPrDetailsModal] = useState(false);
    const [prDetails, setPrDetails] = useState([]);
    const [checkedItems, setCheckedItems] = useState({});
    const calculateTotalDiscount = () => {
        if (!useDiscount) return 0;
        return poItems.reduce((sum, item) => {
            const lineTotal = item.qty * item.unitPrice;
            return sum + (lineTotal * item.detailDiscount) / 100;
        }, 0);
    };

    const handlePRSelect = async (pr) => {
        if (pr.status !== 2) return;

        handleChange("referencePRId", pr.id);
        handleChange("referencePRNo", pr.prNo);
        handleChange("inventoryDestinationPRNo", pr.prNo);

        try {
            const response = await fetch(`${API_BASE_URL}api/PR_Details/PRHeader/${pr.id}`);
            const allDetails = await response.json();

            console.log("✅ Fetched items:", allDetails);

            // Use pendingQtyCache (pre-built from all pending PO headers)
            const availableItems = allDetails.map(item => {
                const approvedUsed = item.useQTY || 0;
                const pendingUsed = pendingQtyCache[item.id] || 0;
                const totalUsed = approvedUsed + pendingUsed;
                const actualAvailable = item.qty - totalUsed;

                console.log(`Item ${item.id}: Qty=${item.qty}, ApprovedUsed=${approvedUsed}, PendingUsed=${pendingUsed}, ActualAvailable=${actualAvailable}`);

                return {
                    ...item,
                    approvedUsed,
                    pendingUsed,
                    remainingQty: actualAvailable,
                    selectedQty: actualAvailable,
                    displayUsedQty: totalUsed,
                    actualAvailable
                };
            });

            const filteredItems = availableItems.filter(item => item.actualAvailable > 0);

            if (filteredItems.length === 0) {
                await Swal.fire({
                    icon: 'info',
                    title: 'No Available Items',
                    text: 'All items from this PR have been fully allocated or reserved by pending POs.',
                    confirmButtonText: 'OK'
                });
                return;
            }

            setPrDetails(filteredItems);

            const initialChecked = {};
            filteredItems.forEach(item => {
                initialChecked[item.id] = true;
            });
            setCheckedItems(initialChecked);

            setShowPRModal(false);
            setPrDetailsModal(true);

        } catch (error) {
            console.error("Error:", error);
        }
    };

    const getPRStatus = (status) => {
        switch (status) {
            case 0:
                return { label: "Pending", className: "bg-gray-100 text-gray-700" };
            case 1:
                return { label: "In Progress", className: "bg-blue-100 text-blue-700" };
            case 2:
                return { label: "Approved", className: "bg-green-100 text-green-700" };
            case 3:
                return { label: "Rejected", className: "bg-red-100 text-red-700" };
            default:
                return { label: "Unknown", className: "bg-gray-200 text-gray-600" };
        }
    };

    const getDestinationLabel = (id) => {
        if (!id) return { label: "No Destination", className: "bg-gray-100 text-gray-500" };

        const dest = inventoryDestinations.find(d => d.destinationId === id);
        if (!dest) return { label: "Unknown Destination", className: "bg-gray-200 text-gray-600" };

        let type = "";
        if (dest.asset) type = "Asset";
        else if (dest.wms) type = "WMS";
        else if (dest.both) type = "Both";

        return {
            label: `${dest.destinationName} (${type})`,
            className: dest.asset
                ? "bg-green-100 text-green-700"
                : dest.wms
                    ? "bg-blue-100 text-blue-700"
                    : dest.both
                        ? "bg-yellow-100 text-yellow-700"
                        : "bg-gray-100 text-gray-500",
        };
    };

    useEffect(() => {
        fetch(`${API_BASE_URL}api/InventoryDestination`)
            .then(res => res.json())
            .then(data => setInventoryDestinations(data))
            .catch(err => console.error(err));
    }, []);

    const [inventoryDestinations, setInventoryDestinations] = useState([]);
    const [showPRModal, setShowPRModal] = useState(false);
    const [prSearch, setPRSearch] = useState("");
    const [prList, setPrList] = useState([]);
    const [isPROpen, setIsPROpen] = useState(false);

    useEffect(() => {
        fetch(`${API_BASE_URL}api/PR_Header`)
            .then(res => res.json())
            .then(data => setPrList(data))
            .catch(err => console.error("Error loading PR Headers:", err));
    }, []);

    const [uoms, setUoms] = useState([]);

    useEffect(() => {
        fetch(`${API_BASE_URL}api/M_Uom`)
            .then((res) => res.json())
            .then((data) => setUoms(data))
            .catch((err) => console.error("UOM fetch error:", err));
    }, []);

    const [selectedApprovers, setSelectedApprovers] = useState([]);
    const toggleApprover = (id) => {
        setSelectedApprovers((prev) =>
            prev.includes(id)
                ? prev.filter((x) => x !== id)
                : [...prev, id]
        );
    };

    const handleInputChange = (e, field) => {
        setFormData({ ...formData, [field]: e.target.value });
    };

    const [companies, setCompanies] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [locations, setLocations] = useState([]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const [poItems, setPoItems] = useState([
        {
            id: 0,
            qty: 0,
            uom: "",
            itemDescription: "",
            specification: "",
            unitPrice: 0,
            detailDiscount: 0,
            serveStatus: "Pending",
            lastServedDate: new Date().toISOString(),
            chargingVisa: "",
            costEstimate: 0,
            totalReceivedQty: 0,
            referencePRDetailsId: 0
        }
    ]);

    const [isVatInclusive, setIsVatInclusive] = useState(false);
    const [useDiscount, setUseDiscount] = useState(false);
    const [isNonVat, setIsNonVat] = useState(true);
    const addNewItem = () => {
        setPoItems([...poItems, {
            id: 0,
            qty: 0,
            uom: "",
            itemDescription: "",
            specification: "",
            unitPrice: 0,
            detailDiscount: 0,
            serveStatus: "Pending",
            lastServedDate: new Date().toISOString(),
            chargingVisa: "",
            costEstimate: 0,
            totalReceivedQty: 0,
            referencePRDetailsId: 0
        }]);
    };

    const removeItem = (index) => {
        const updated = poItems.filter((_, i) => i !== index);
        setPoItems(updated);
    };

    const updateItem = (index, field, value) => {
        const updated = [...poItems];
        updated[index][field] = value;
        setPoItems(updated);
    };

    const calculateSubtotal = () => {
        return poItems.reduce((sum, item) => {
            const lineTotal = item.qty * item.unitPrice;
            const discount = useDiscount ? (lineTotal * item.detailDiscount) / 100 : 0;
            return sum + (lineTotal - discount);
        }, 0);
    };

    const calculateVAT = () => {
        if (isNonVat) return 0;
        if (!isVatInclusive) return 0;  // hindi checked = walang VAT, wag mag-add
        return calculateSubtotal() * 0.12;  // checked lang = may dagdag na 12%
    };

    const calculateTotal = () => {
        const subtotal = calculateSubtotal();
        const vat = calculateVAT();
        return subtotal + vat;
    };

    const [activeTab, setActiveTab] = useState("details");
    const [savedDrafts, setSavedDrafts] = useState([]);
    const [showDraftModal, setShowDraftModal] = useState(false);

    const saveDraft = () => {
        const draft = {
            id: Date.now(),
            timestamp: new Date().toISOString(),
            formData: formData,
            poItems: poItems,
            isVatInclusive: isVatInclusive,
            useDiscount: useDiscount,
            isNonVat: isNonVat
        };

        const drafts = JSON.parse(localStorage.getItem('po_drafts') || '[]');
        drafts.push(draft);
        localStorage.setItem('po_drafts', JSON.stringify(drafts));

        loadDrafts();

        Swal.fire({
            icon: 'success',
            title: 'Draft Saved',
            text: `Draft saved successfully! PO: ${formData.poNumber}`,
            timer: 2000,
            showConfirmButton: false
        });
    };

    const loadDrafts = () => {
        const drafts = JSON.parse(localStorage.getItem('po_drafts') || '[]');
        setSavedDrafts(drafts);
    };

    const loadDraft = (draft) => {
        setFormData(draft.formData);
        setPoItems(draft.poItems);
        setIsVatInclusive(draft.isVatInclusive);
        setUseDiscount(draft.useDiscount);
        setIsNonVat(draft.isNonVat);
        setShowDraftModal(false);

        Swal.fire({
            icon: 'success',
            title: 'Draft Loaded',
            text: 'Draft loaded successfully!',
            timer: 2000,
            showConfirmButton: false
        });
    };

    const deleteDraft = async (draftId) => {
        const result = await Swal.fire({
            title: 'Delete Draft?',
            text: 'This will permanently delete the draft.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel'
        });

        if (!result.isConfirmed) return;

        const drafts = JSON.parse(localStorage.getItem('po_drafts') || '[]');
        const filtered = drafts.filter(d => d.id !== draftId);
        localStorage.setItem('po_drafts', JSON.stringify(filtered));
        loadDrafts();

        Swal.fire({
            icon: 'success',
            title: 'Deleted',
            text: 'Draft deleted successfully!',
            timer: 2000,
            showConfirmButton: false
        });
    };

    useEffect(() => {
        loadDrafts();
    }, []);

    const handleSubmit = async () => {
        try {
            const user = JSON.parse(localStorage.getItem("user"));
            const userId = user?.userId || 0;

            const headerPayload = {
                id: 0,
                poNumber: formData.poNumber,
                inventoryDestinationPRId: formData.inventoryDestinationPRNo || "",
                poCreatedDate: new Date(formData.poCreatedDate).toISOString(),
                referencePRId: parseInt(formData.referencePRId) || 0,
                companyId: parseInt(formData.companyId) || 0,
                vendorId: parseInt(formData.vendorId) || 0,
                terms: formData.terms,
                deliveryDate: new Date(formData.deliveryDate).toISOString(),
                shipTo_Location: formData.shipTo_Location,
                shipTo_Address1: formData.shipTo_Address1,
                shipTo_ContactPerson: formData.shipTo_ContactPerson,
                shipTo_ContactNo: formData.shipTo_ContactNumber,
                remarks: formData.remarks,
                isVatInclusive: isVatInclusive,
                headerDiscount: parseFloat(formData.headerDiscount) || 0,
                preparedBy: userId,
                status: parseInt(formData.status) || 0,
                isPOPrinted: formData.isPOPrinted,
                reprintCount: parseInt(formData.reprintCount) || 0,
                dateSubmittedForApproval: new Date().toISOString(),
                receivingStatus: formData.receivingStatus,
                departmentId: parseInt(formData.departmentId) || 0,
                locationId: parseInt(formData.locationId) || 0,
                isFromQuotation: parseInt(formData.isFromQuotation) || 0,
                isNonVAT: isNonVat,
                paymentMode: formData.paymentMode,
                tin: formData.tin
            };

            console.log("📤 Sending PO Header Payload:", JSON.stringify(headerPayload, null, 2));

            const headerResponse = await fetch(`${API_BASE_URL}api/T_PO_Header`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "accept": "text/plain"
                },
                body: JSON.stringify(headerPayload)
            });

            console.log("📥 Response Status:", headerResponse.status);

            if (!headerResponse.ok) {
                const errorText = await headerResponse.text();
                console.error("❌ Backend Error Response:", errorText);
                throw new Error(`Failed to create PO Header: ${errorText}`);
            }

            const createdHeader = await headerResponse.json();
            const poHeaderId = createdHeader.id;

            // Create PO Details (bulk)
            const detailsPayload = poItems.map(item => ({
                id: 0,
                poHeaderId: poHeaderId,
                qty: parseFloat(item.qty) || 0,
                uom: item.uom || "",
                itemDescription: item.itemDescription || "",
                specification: item.specification || "",
                itemCode: item.itemCode || "",
                unitPrice: parseFloat(item.unitPrice) || 0,
                detailDiscount: parseFloat(item.detailDiscount) || 0,
                serveStatus: "Pending",
                lastServedDate: new Date().toISOString(),
                chargingVisa: "",
                costEstimate: parseFloat(calculateTotal()) || 0,
                totalReceivedQty: 0,
                referencePRDetailsId: parseInt(item.referencePRDetailsId) || 0,
            }));

            console.log("📦 PO Details Payload:", JSON.stringify(detailsPayload, null, 2));

            const detailsResponse = await fetch(`${API_BASE_URL}api/T_Po_Details/bulk`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "accept": "text/plain"
                },
                body: JSON.stringify(detailsPayload)
            });

            if (!detailsResponse.ok) {
                const errorText = await detailsResponse.text();
                console.error("❌ Details Error Response:", errorText);
                throw new Error(`Failed to create PO Details: ${errorText}`);
            }

            // ✅ NO UseQty update here!
            // UseQty is only updated when the PO is APPROVED (in POApprovalModal)
            // and restored when REJECTED. This keeps availability accurate.

            await Swal.fire({
                icon: "success",
                title: "Purchase Order Created",
                text: "The Purchase Order was successfully created.",
                confirmButtonText: "OK",
            });

            // Reset form
            setFormData({
                poNumber: generatePoNumber(),
                poCreatedDate: new Date().toISOString().split('T')[0],
                referencePRId: 0,
                inventoryDestinationPRId: 0,
                paymentMode: "",
                tin: "",
                companyId: "", company_Address1: "", company_Address2: "",
                company_TIN: "", company_ContactNumber: "", company_Email: "",
                departmentId: "", locationId: "",
                vendorId: "", vendor_Address1: "", vendor_Address2: "",
                vendor_ContactPerson: "", vendor_ContactNumber: "", terms: "",
                shipTo_Location: "", shipTo_Address1: "", shipTo_Address2: "",
                shipTo_ContactPerson: "", shipTo_ContactNumber: "",
                deliveryDate: today,
                remarks: "", isVatInclusive: true, headerDiscount: 0,
                preparedBy: 0, status: 0, isPOPrinted: false,
                reprintCount: 0, receivingStatus: "Pending",
                isFromQuotation: 0, isNonVAT: false
            });
            setPoItems([{
                id: 0, qty: 0, uom: "", itemDescription: "",
                specification: "", unitPrice: 0, detailDiscount: 0,
                serveStatus: "Pending", lastServedDate: new Date().toISOString(),
                chargingVisa: "", costEstimate: 0, totalReceivedQty: 0,
                referencePRDetailsId: 0
            }]);
            setIsVatInclusive(false);
            setUseDiscount(false);
            setIsNonVat(false);
            setActiveTab("details");

            window.location.reload();

        } catch (error) {
            console.error("❌ Full Error:", error);
            await Swal.fire({
                icon: "error",
                title: "Failed to Create PO",
                text: error.message,
                confirmButtonText: "OK",
            });
        }
    };

    const [companyOpen, setCompanyOpen] = useState(false);
    const [companySearch, setCompanySearch] = useState("");

    useEffect(() => {
        const fetchCompanies = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}api/Company`);
                const data = await response.json();
                setCompanies(data);
            } catch (error) {
                console.error("Error fetching companies:", error);
            }
        };
        fetchCompanies();
    }, []);

    const [vendorSearch, setVendorSearch] = useState("");
    const [isVendorOpen, setIsVendorOpen] = useState(false);

    useEffect(() => {
        fetch(`${API_BASE_URL}api/Vendor`)
            .then(res => res.json())
            .then(data => setVendors(data))
            .catch(err => console.error("Vendor load error:", err));
    }, []);

    const [showVendorModal, setShowVendorModal] = useState(false);

    const handleVendorSelect = (vendor) => {
        handleChange("vendorId", vendor.id);
        handleChange("vendorName", vendor.VendorName);
        handleChange("tin", vendor.TIN || "");
        handleChange("vendor_Address1", vendor.Address1 || "");
        handleChange("vendor_Address2", vendor.Address2 || "");
        handleChange("vendor_ContactPerson", vendor.ContactPerson || "");
        handleChange("vendor_ContactNumber", vendor.ContactNo || "");
        handleChange("terms", vendor.PaymentTerms || "");
        handleChange("vendor_Email", vendor.Email || "");
        handleChange("vendor_FaxNo", vendor.FaxNo || "");
        setShowVendorModal(false);
    };

    const [isDeptOpen, setIsDeptOpen] = useState(false);
    const [deptSearch, setDeptSearch] = useState("");
    const [isLocationOpen, setIsLocationOpen] = useState(false);
    const [locationSearch, setLocationSearch] = useState("");

    useEffect(() => {
        const fetchDepartments = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}api/Department`);
                const data = await response.json();
                setDepartments(data);
            } catch (error) {
                console.error("Error fetching departments:", error);
            }
        };
        fetchDepartments();
    }, []);

    useEffect(() => {
        const fetchLocations = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}api/Location`);
                const data = await response.json();
                setLocations(data);
            } catch (error) {
                console.error("Error fetching locations:", error);
            }
        };
        fetchLocations();
    }, []);

    const [loading, setLoading] = useState(true);
    const [shippingInfo, setShippingInfo] = useState([]);
    const [shippingLocations, setShippingLocations] = useState([]);

    useEffect(() => {
        const fetchShippingInfo = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}api/ShippingInfo`);
                const activeLocations = response.data.filter(info => info.isActive === 1);
                setShippingInfo(activeLocations);
                setShippingLocations(activeLocations.map(info => info.shipTo_Location));
            } catch (error) {
                console.error("Error fetching shipping info:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchShippingInfo();
    }, []);

    const handleLocationSelect = (locationName) => {
        handleChange("shipTo_Location", locationName);
        const selected = shippingInfo.find(info => info.shipTo_Location === locationName);
        if (selected) {
            handleChange("shipTo_Address1", selected.shipTo_Address1 || "");
            handleChange("shipTo_Address2", selected.shipTo_Address2 || "");
            handleChange("shipTo_ContactPerson", selected.shipTo_ContactName || "");
            handleChange("shipTo_ContactNumber", selected.shipTo_ContactNo || "");
        }
    };

    const today = new Date().toISOString().split("T")[0];

    useEffect(() => {
        if (!formData.deliveryDate) {
            handleChange("deliveryDate", today);
        }
    }, []);

    const generatePoNumber = () => {
        const now = new Date();
        const yyyy = now.getFullYear();
        const mm = String(now.getMonth() + 1).padStart(2, "0");
        const dd = String(now.getDate()).padStart(2, "0");
        const hh = String(now.getHours()).padStart(2, "0");
        const min = String(now.getMinutes()).padStart(2, "0");
        const ss = String(now.getSeconds()).padStart(2, "0");
        return `PO-${yyyy}${mm}${dd}-${hh}${min}${ss}`;
    };

    useEffect(() => {
        if (!formData.poNumber) {
            handleChange("poNumber", generatePoNumber());
        }
        if (!formData.poCreatedDate) {
            handleChange("poCreatedDate", today);
        }
    }, []);

    const [deleteConfirmModal, setDeleteConfirmModal] = useState(null);

    const confirmDelete = (draftId) => {
        setDeleteConfirmModal(draftId);
    };

    const handleDeleteConfirm = () => {
        if (deleteConfirmModal) {
            deleteDraft(deleteConfirmModal);
            setDeleteConfirmModal(null);
        }
    };

    const [prAvailability, setPrAvailability] = useState({});
    const [pendingQtyCache, setPendingQtyCache] = useState({});

    // ✅ Step 1: Fetch ALL PO headers, find pending ones (status 0 or 1),
    // fetch their details, build cache: { prDetailId -> pendingQty }
    useEffect(() => {
        const buildPendingCache = async () => {
            try {
                const headersRes = await fetch(`${API_BASE_URL}api/T_PO_Header`);
                if (!headersRes.ok) return;
                const allHeaders = await headersRes.json();

                // Only pending POs (not yet approved)
                const pendingHeaders = allHeaders.filter(h => h.status === 0 || h.status === 1);
                if (pendingHeaders.length === 0) return;

                const cache = {};
                await Promise.all(pendingHeaders.map(async (header) => {
                    try {
                        const detailsRes = await fetch(`${API_BASE_URL}api/T_Po_Details/POHeader/${header.id}`);
                        if (!detailsRes.ok) return;
                        const details = await detailsRes.json();
                        details.forEach(detail => {
                            if (detail.referencePRDetailsId) {
                                const key = detail.referencePRDetailsId;
                                cache[key] = (cache[key] || 0) + (detail.qty || 0);
                            }
                        });
                    } catch { /* skip */ }
                }));

                console.log("📦 Pending qty cache:", cache);
                setPendingQtyCache(cache);
            } catch (err) {
                console.error("Error building pending cache:", err);
            }
        };

        buildPendingCache();
    }, []);

    // ✅ Step 2: Compute availability using prList + pendingQtyCache
    useEffect(() => {
        if (prList.length === 0) return;

        const checkAvailability = async () => {
            const result = {};
            const approvedPRs = prList.filter(pr => pr.status === 2);

            await Promise.all(approvedPRs.map(async (pr) => {
                try {
                    const res = await fetch(`${API_BASE_URL}api/PR_Details/PRHeader/${pr.id}`);
                    const items = await res.json();

                    const availableItems = items.map(item => {
                        const approvedUsed = item.useQTY || 0;
                        const pendingUsed = pendingQtyCache[item.id] || 0;
                        const actualAvailable = item.qty - approvedUsed - pendingUsed;

                        return {
                            itemDescription: item.itemDescription,
                            total: item.qty,
                            approvedUsed,
                            pendingUsed,
                            available: actualAvailable
                        };
                    }).filter(item => item.available > 0);

                    result[pr.id] = { hasAvailable: availableItems.length > 0, items: availableItems };
                } catch {
                    result[pr.id] = { hasAvailable: false, items: [] };
                }
            }));

            setPrAvailability(result);
        };

        checkAvailability();
    }, [prList, pendingQtyCache]);

    const [currentPage, setCurrentPage] = useState(1);

    return (
        <div className="min-h-screen  to-blue-50 p-8">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl shadow-2xl p-8 mb-2">
                <div className="flex items-start justify-between">
                    <div>
                        <h1 className="text-4xl font-bold text-white flex items-center gap-3">
                            <FileText size={40} />
                            Purchase Order
                        </h1>
                        <p className="text-blue-100 mt-2">Create new purchase order request</p>
                    </div>
                    <div className="text-right">
                        <h2 className="text-2xl font-bold text-white">{formData.poNumber || 'PO-XXXX'}</h2>
                        <p className="text-blue-100 text-sm mt-1">
                            {formData.poCreatedDate || new Date().toLocaleDateString()}
                        </p>
                    </div>
                </div>
            </div>

            <div className="flex bg-white px-6 md:px-8 gap-2 justify-start items-center overflow-x-auto border-b border-gray-200">
                <button
                    onClick={() => setActiveTab("details")}
                    className={`relative px-4 py-3 font-semibold whitespace-nowrap transition-all duration-200 ${activeTab === "details" ? "text-blue-700" : "text-gray-500 hover:text-blue-600"}`}
                >
                    Company & Vendor Details
                    {activeTab === "details" && (
                        <div className="absolute bottom-0 left-0 right-0 h-1 bg-blue-600 transform skew-x-[-20deg] rounded"></div>
                    )}
                </button>

                <span className="text-gray-300 font-bold select-none">/</span>

                <button
                    onClick={() => setActiveTab("items")}
                    className={`relative px-4 py-3 font-semibold whitespace-nowrap transition-all duration-200 ${activeTab === "items" ? "text-blue-700" : "text-gray-500 hover:text-blue-600"}`}
                >
                    Purchase Order Items
                    {activeTab === "items" && (
                        <div className="absolute bottom-0 left-0 right-0 h-1 bg-blue-600 transform skew-x-[-20deg] rounded"></div>
                    )}
                </button>

                <span className="text-gray-300 font-bold select-none">/</span>

                <button
                    onClick={() => setActiveTab("remarks")}
                    className={`relative px-4 py-3 font-semibold whitespace-nowrap transition-all duration-200 ${activeTab === "remarks" ? "text-blue-700" : "text-gray-500 hover:text-blue-600"}`}
                >
                    Remarks & Approvals
                    {activeTab === "remarks" && (
                        <div className="absolute bottom-0 left-0 right-0 h-1 bg-blue-600 transform skew-x-[-20deg] rounded"></div>
                    )}
                </button>
            </div>

            <div className="mb-8">
                {activeTab === "details" && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
                        {/* Card 1: Charging Company */}
                        <div className="bg-white rounded-xl shadow-lg border border-blue-100 overflow-hidden">
                            <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-2">
                                <h3 className="text-base font-bold text-white flex items-center gap-2">
                                    <Building2 size={18} />
                                    Bill To
                                </h3>
                            </div>
                            <div className="p-3 space-y-2">
                                <div className="relative">
                                    <label className="block text-xs font-bold text-gray-700 mb-1">
                                        Company: <span className="text-red-500">*</span>
                                    </label>
                                    <div
                                        onClick={() => setCompanyOpen(!companyOpen)}
                                        className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg bg-white cursor-pointer flex justify-between items-center"
                                    >
                                        {formData.companyId
                                            ? companies.find(c => c.id == formData.companyId)?.companyName
                                            : "Select Company"}
                                        <span className="text-gray-500">▼</span>
                                    </div>

                                    {companyOpen && (
                                        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                                            <div className="p-2 border-b">
                                                <input
                                                    type="text"
                                                    placeholder="Search..."
                                                    value={companySearch}
                                                    onChange={(e) => setCompanySearch(e.target.value)}
                                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500"
                                                />
                                            </div>
                                            <div>
                                                {companies
                                                    .filter((c) => c.companyName?.toLowerCase().includes(companySearch.toLowerCase()))
                                                    .map((company) => (
                                                        <div
                                                            key={company.id}
                                                            onClick={() => {
                                                                handleChange("companyId", company.id);
                                                                handleChange("company_Address1", company.address1 || "");
                                                                handleChange("company_Address2", company.address2 || "");
                                                                handleChange("company_TIN", company.tin || "");
                                                                handleChange("company_ContactNumber", company.contactNo || "");
                                                                handleChange("company_Email", company.email || "");
                                                                setCompanyOpen(false);
                                                            }}
                                                            className="px-3 py-2 text-sm hover:bg-blue-100 cursor-pointer"
                                                        >
                                                            {company.companyName}
                                                        </div>
                                                    ))}
                                                {companies.filter((c) =>
                                                    c.companyName.toLowerCase().includes(companySearch.toLowerCase())
                                                ).length === 0 && (
                                                        <div className="px-3 py-2 text-sm text-gray-400">No results found</div>
                                                    )}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1">Address 1:</label>
                                    <input
                                        type="text"
                                        value={formData.company_Address1}
                                        onChange={(e) => handleChange("company_Address1", e.target.value)}
                                        className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                        placeholder="Street Address"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1">TIN Number:</label>
                                    <input
                                        type="text"
                                        value={formData.company_TIN}
                                        onChange={(e) => handleChange("company_TIN", e.target.value)}
                                        className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                        placeholder="000-000-000-000"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1">Contact Number:</label>
                                    <input
                                        type="text"
                                        value={formData.company_ContactNumber}
                                        onChange={(e) => handleChange("company_ContactNumber", e.target.value)}
                                        className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                        placeholder="+63 XXX XXX XXXX"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1">Email:</label>
                                    <input
                                        type="email"
                                        value={formData.company_Email}
                                        onChange={(e) => handleChange("company_Email", e.target.value)}
                                        className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                        placeholder="company@email.com"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Card 2: Vendor Information */}
                        <div className="bg-white rounded-xl shadow-lg border border-blue-100 overflow-hidden">
                            <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-2">
                                <h3 className="text-base font-bold text-white flex items-center gap-2">
                                    <User size={18} />
                                    Vendor Information
                                </h3>
                            </div>
                            <div className="p-3 space-y-2">
                                <div className="relative">
                                    <label className="block text-xs font-bold text-gray-700 mb-1">
                                        Vendor: <span className="text-red-500">*</span>
                                    </label>
                                    <div
                                        className="px-2 py-2 border text-sm border-gray-300 rounded-lg cursor-pointer bg-white flex justify-between items-center"
                                        onClick={() => setShowVendorModal(true)}
                                    >
                                        {formData.vendorId
                                            ? vendors.find((v) => v.id === formData.vendorId)?.VendorName
                                            : "Select Vendor"}
                                        <span className="text-gray-500">🔍</span>
                                    </div>

                                    {showVendorModal && (
                                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                                            <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[80vh] overflow-hidden m-4">
                                                <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4 flex justify-between items-center">
                                                    <h3 className="text-xl font-bold text-white">Select Vendor</h3>
                                                    <button onClick={() => setShowVendorModal(false)} className="text-white text-2xl">✕</button>
                                                </div>
                                                <div className="p-4 border-b">
                                                    <input
                                                        type="text"
                                                        placeholder="Search vendor..."
                                                        value={vendorSearch}
                                                        onChange={(e) => setVendorSearch(e.target.value)}
                                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                                    />
                                                </div>
                                                <div className="overflow-y-auto max-h-[55vh] p-4 space-y-2">
                                                    {vendors
                                                        .filter((v) => v.VendorName?.toLowerCase().includes(vendorSearch.toLowerCase()))
                                                        .map((vendor) => (
                                                            <div
                                                                key={vendor.id}
                                                                onClick={() => {
                                                                    handleVendorSelect(vendor);
                                                                    setShowVendorModal(false);
                                                                }}
                                                                className="border border-gray-200 rounded-lg p-3 hover:bg-blue-50 hover:border-blue-300 cursor-pointer transition-all"
                                                            >
                                                                <p className="font-bold text-blue-700">{vendor.VendorName}</p>
                                                                <p className="text-xs text-gray-500">{vendor.VendorCode} | {vendor.Address1 || "No address"}</p>
                                                            </div>
                                                        ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1">Address 1:</label>
                                    <input
                                        type="text"
                                        value={formData.vendor_Address1}
                                        onChange={(e) => handleChange("vendor_Address1", e.target.value)}
                                        className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                        placeholder="Street Address"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1">Contact Person:</label>
                                    <input
                                        type="text"
                                        value={formData.vendor_ContactPerson}
                                        onChange={(e) => handleChange("vendor_ContactPerson", e.target.value)}
                                        className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                        placeholder="Contact Person Name"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1">Contact Number:</label>
                                    <input
                                        type="text"
                                        value={formData.vendor_ContactNumber}
                                        onChange={(e) => handleChange("vendor_ContactNumber", e.target.value)}
                                        className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                        placeholder="+63 XXX XXX XXXX"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1">Tin:</label>
                                    <input
                                        type="text"
                                        value={formData.tin}
                                        onChange={(e) => handleChange("tin", e.target.value)}
                                        className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                        placeholder=" XXX XXX XXXX"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1">
                                        Payment Mode:<span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        value={formData.paymentMode || ""}
                                        onChange={(e) => handleChange("paymentMode", e.target.value)}
                                        className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                    >
                                        <option value="">Select Payment Mode</option>
                                        <option value="CASH">CASH</option>
                                        <option value="GCASH">GCASH</option>
                                        <option value="BANK TRANSFER">BANK TRANSFER</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1">
                                        Terms:<span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.terms}
                                        onChange={(e) => handleChange("terms", e.target.value)}
                                        className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                        placeholder="e.g., Net 30, COD, etc."
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Card 3: Ship To Details */}
                        <div className="bg-white rounded-xl shadow-lg border border-blue-100 overflow-hidden">
                            <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-2">
                                <h3 className="text-base font-bold text-white flex items-center gap-2">
                                    <Truck size={18} />
                                    Ship To Details
                                </h3>
                            </div>
                            <div className="p-3 space-y-2">
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1">Location:</label>
                                    <div className="flex gap-1">
                                        <select
                                            value={formData.shipTo_Location}
                                            onChange={(e) => handleLocationSelect(e.target.value)}
                                            className="flex-1 px-2 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                        >
                                            <option value="">Select a location</option>
                                            {loading ? (
                                                <option>Loading...</option>
                                            ) : (
                                                shippingLocations.map((loc, index) => (
                                                    <option key={index} value={loc}>{loc}</option>
                                                ))
                                            )}
                                        </select>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-green-600 mb-1">
                                        Address 1: <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.shipTo_Address1}
                                        onChange={(e) => handleChange("shipTo_Address1", e.target.value)}
                                        className="w-full px-2 py-2 text-sm border border-green-300 rounded-lg focus:ring-1 focus:ring-green-500 focus:border-green-500 transition-all"
                                        placeholder="Street Address"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-green-600 mb-1">
                                        Contact Person: <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.shipTo_ContactPerson}
                                        onChange={(e) => handleChange("shipTo_ContactPerson", e.target.value)}
                                        className="w-full px-2 py-2 text-sm border border-green-300 rounded-lg focus:ring-1 focus:ring-green-500 focus:border-green-500 transition-all"
                                        placeholder="Contact Person Name"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-green-600 mb-1">
                                        Contact Number: <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.shipTo_ContactNumber}
                                        onChange={(e) => handleChange("shipTo_ContactNumber", e.target.value)}
                                        className="w-full px-2 py-2 text-sm border border-green-300 rounded-lg focus:ring-1 focus:ring-green-500 focus:border-green-500 transition-all"
                                        placeholder="+63 XXX XXX XXXX"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1">Delivery Date:</label>
                                    <input
                                        type="date"
                                        value={formData.deliveryDate || today}
                                        onChange={(e) => handleChange("deliveryDate", e.target.value)}
                                        className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                    />
                                </div>
                            </div>
                        </div>

                        <div>
                            <label className="block text font-bold text-gray-700 mb-1">
                                Reference Inventory Destination: <span className="text-red-500">*</span>
                            </label>
                            <div
                                onClick={() => setShowPRModal(true)}
                                className="px-2 py-2 border text-sm border-gray-300 rounded-lg cursor-pointer bg-white flex justify-between items-center hover:border-blue-500 transition-all"
                            >
                                {formData.referencePRId
                                    ? prList.find(pr => pr.id === formData.referencePRId)?.prNo
                                    : "Select PR"}
                                <span className="text-gray-500">🔍</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* PR Selection Modal */}
                {showPRModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[80vh] overflow-hidden m-4">
                            <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4 flex justify-between items-center">
                                <h3 className="text-xl font-bold text-white">Select Inventory Destination</h3>
                                <button onClick={() => setShowPRModal(false)} className="text-white hover:text-gray-200 text-2xl">✕</button>
                            </div>

                            <div className="p-4 border-b">
                                <input
                                    type="text"
                                    placeholder="Search PR Number..."
                                    value={prSearch}
                                    onChange={(e) => { setPRSearch(e.target.value); setCurrentPage(1); }}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            <div className="p-4 overflow-y-auto max-h-[50vh]">
                                {(() => {
                                    const filteredPRs = prList.filter(pr =>
                                        pr.status === 2 &&
                                        pr.prNo.toLowerCase().includes(prSearch.toLowerCase()) &&
                                        prAvailability[pr.id]?.hasAvailable === true
                                    );

                                    const itemsPerPage = 5;
                                    const totalPages = Math.ceil(filteredPRs.length / itemsPerPage);
                                    const startIndex = (currentPage - 1) * itemsPerPage;
                                    const paginatedPRs = filteredPRs.slice(startIndex, startIndex + itemsPerPage);

                                    if (filteredPRs.length === 0) {
                                        return <p className="text-center text-gray-500 py-8">No approved PR found</p>;
                                    }

                                    return (
                                        <>
                                            <div className="space-y-2">
                                                {paginatedPRs.map((pr) => (
                                                    <div
                                                        key={pr.id}
                                                        onClick={() => handlePRSelect(pr)}
                                                        className="border border-gray-200 rounded-lg p-4 transition-all hover:bg-blue-50 hover:border-blue-300 cursor-pointer"
                                                    >
                                                        <div className="flex justify-between items-start">
                                                            <div className="flex-1">
                                                                <p className="font-bold text-blue-700 text-lg">{pr.prNo}</p>
                                                                <p className="text-sm text-gray-600 mt-1">
                                                                    Company ID: {pr.companyId} | Created: {new Date(pr.creationDate).toLocaleDateString()}
                                                                </p>
                                                                <p className="text-xs text-gray-500 mt-1">
                                                                    Date Needed: {new Date(pr.dateNeeded).toLocaleDateString()}
                                                                </p>
                                                                {prAvailability[pr.id]?.items?.length > 0 && (
                                                                    <div className="mt-2 space-y-1">
                                                                        {prAvailability[pr.id].items.map((item, i) => (
                                                                            <div key={i} className="flex flex-wrap items-center gap-1 text-xs">
                                                                                <span className="text-gray-600">• {item.itemDescription}</span>
                                                                                {/* FREE / actually available */}
                                                                                <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-bold">
                                                                                    {item.available} free
                                                                                </span>
                                                                                {/* PENDING - reserved but not yet approved */}
                                                                                {item.pendingUsed > 0 && (
                                                                                    <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full font-bold">
                                                                                        {item.pendingUsed} pending PO
                                                                                    </span>
                                                                                )}
                                                                                {/* APPROVED - already consumed */}
                                                                                {item.approvedUsed > 0 && (
                                                                                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-bold">
                                                                                        {item.approvedUsed} approved
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="flex flex-col gap-2 items-end">
                                                                <span className={`px-4 py-2 rounded-full text-sm font-semibold ${getDestinationLabel(pr.inventoryDestinationId).className}`}>
                                                                    {(() => {
                                                                        const label = getDestinationLabel(pr.inventoryDestinationId).label;
                                                                        const match = label.match(/^(.*)\s\((.*)\)$/);
                                                                        if (!match) return label;
                                                                        return (
                                                                            <>
                                                                                <span>{match[1]}</span>{" "}
                                                                                <span className="ml-1 px-2 py-0.5 text-xs font-bold rounded-full bg-white text-black">{match[2]}</span>
                                                                            </>
                                                                        );
                                                                    })()}
                                                                </span>
                                                                <span className="px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-800">Approved</span>
                                                                {prAvailability[pr.id] && (
                                                                    <span className="px-3 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700">
                                                                        {prAvailability[pr.id].items.length} item/s available
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>

                                            {totalPages > 1 && (
                                                <div className="flex justify-center items-center gap-2 mt-4 pt-4 border-t">
                                                    <button
                                                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                                        disabled={currentPage === 1}
                                                        className="px-3 py-1 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        Previous
                                                    </button>
                                                    <span className="text-sm text-gray-600">Page {currentPage} of {totalPages}</span>
                                                    <button
                                                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                                        disabled={currentPage === totalPages}
                                                        className="px-3 py-1 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        Next
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    );
                                })()}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* PR Details Modal */}
            {prDetailsModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[80vh] overflow-hidden m-4">
                        <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4 flex justify-between items-center">
                            <h3 className="text-xl font-bold text-white">
                                Select Items from {formData.referencePRNo}
                            </h3>
                            <button onClick={() => setPrDetailsModal(false)} className="text-white hover:text-gray-200 text-2xl">✕</button>
                        </div>

                        <div className="p-4 overflow-y-auto max-h-[60vh]">
                            <table className="w-full">
                                <thead className="bg-gray-100 sticky top-0">
                                    <tr>
                                        <th className="p-3 text-left">
                                            <input
                                                type="checkbox"
                                                checked={Object.values(checkedItems).every(v => v)}
                                                onChange={(e) => {
                                                    const newChecked = {};
                                                    prDetails.forEach(item => { newChecked[item.id] = e.target.checked; });
                                                    setCheckedItems(newChecked);
                                                }}
                                                className="w-5 h-5"
                                            />
                                        </th>
                                        <th className="p-3 text-left font-bold">Item Code</th>
                                        <th className="p-3 text-left font-bold">Original Qty</th>
                                        <th className="p-3 text-left font-bold">Used Qty</th>
                                        <th className="p-3 text-left font-bold text-green-600">Available</th>
                                        <th className="p-3 text-left font-bold text-blue-600">To Use</th>
                                        <th className="p-3 text-left font-bold">UOM</th>
                                        <th className="p-3 text-left font-bold">Item Description</th>
                                        <th className="p-3 text-left font-bold">Specification</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {prDetails.filter((item) => item.actualAvailable > 0).map((item) => {
                                        const availableQty = item.actualAvailable;

                                        return (
                                            <tr key={item.id} className="border-b hover:bg-gray-50">
                                                <td className="p-3">
                                                    <input
                                                        type="checkbox"
                                                        checked={checkedItems[item.id] || false}
                                                        onChange={(e) => setCheckedItems({ ...checkedItems, [item.id]: e.target.checked })}
                                                        className="w-5 h-5"
                                                    />
                                                </td>
                                                <td className="p-3 text-gray-600">{item.itemCode}</td>
                                                <td className="p-3 text-gray-600">{item.qty}</td>
                                                {/* Used Qty breakdown */}
                                                <td className="p-3">
                                                    <div className="flex flex-col gap-0.5">
                                                        <span className="text-orange-600 font-semibold">{item.displayUsedQty || 0}</span>
                                                        {item.approvedUsed > 0 && (
                                                            <span className="text-xs text-blue-600 whitespace-nowrap">✅ {item.approvedUsed} approved</span>
                                                        )}
                                                        {item.pendingUsed > 0 && (
                                                            <span className="text-xs text-orange-500 whitespace-nowrap">⏳ {item.pendingUsed} pending</span>
                                                        )}
                                                    </div>
                                                </td>
                                                {/* Actual available - deducted pending + approved */}
                                                <td className="p-3">
                                                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full font-bold">{availableQty}</span>
                                                </td>
                                                <td className="p-3">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max={availableQty}
                                                        value={item.selectedQty || 0}
                                                        onChange={(e) => {
                                                            const inputValue = parseFloat(e.target.value) || 0;
                                                            if (inputValue > availableQty) {
                                                                Swal.fire({
                                                                    icon: 'warning',
                                                                    title: 'Exceeded Available Quantity',
                                                                    text: `Only ${availableQty} available. You entered ${inputValue}.`,
                                                                    timer: 2000,
                                                                    showConfirmButton: false
                                                                });
                                                                return;
                                                            }
                                                            const updated = prDetails.map(d =>
                                                                d.id === item.id ? { ...d, selectedQty: inputValue } : d
                                                            );
                                                            setPrDetails(updated);
                                                        }}
                                                        className="w-24 px-2 py-1 border border-blue-300 rounded focus:ring-2 focus:ring-blue-500 font-semibold"
                                                    />
                                                </td>
                                                <td className="p-3">
                                                    <select
                                                        value={item.uom}
                                                        onChange={(e) => {
                                                            const updated = prDetails.map(d =>
                                                                d.id === item.id ? { ...d, uom: e.target.value } : d
                                                            );
                                                            setPrDetails(updated);
                                                        }}
                                                        disabled
                                                        className="px-2 py-1 border rounded"
                                                    >
                                                        {uoms.map((u) => (
                                                            <option key={u.id} value={u.uom}>{u.uom}</option>
                                                        ))}
                                                    </select>
                                                </td>
                                                <td className="p-3">{item.itemDescription}</td>
                                                <td className="p-3">{item.materialFinish}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        <div className="p-4 bg-gray-50 flex justify-end gap-3">
                            <button
                                onClick={() => setPrDetailsModal(false)}
                                className="px-6 py-2 bg-gray-300 rounded-lg hover:bg-gray-400"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={async () => {
                                    try {
                                        const selected = prDetails.filter(item => checkedItems[item.id]);

                                        if (selected.length === 0) {
                                            await Swal.fire({ icon: 'warning', title: 'No Items Selected', text: 'Please select at least one item.', confirmButtonText: 'OK' });
                                            return;
                                        }

                                        const hasInvalidQty = selected.some(item => !item.selectedQty || item.selectedQty <= 0);
                                        if (hasInvalidQty) {
                                            await Swal.fire({ icon: 'warning', title: 'Invalid Quantity', text: 'Please enter a valid quantity for all selected items.', confirmButtonText: 'OK' });
                                            return;
                                        }

                                        const mappedItems = selected.map(item => ({
                                            id: 0,
                                            qty: item.selectedQty,
                                            uom: item.uom,
                                            itemDescription: item.itemDescription,
                                            specification: item.materialFinish,
                                            itemCode: item.itemCode || "",
                                            unitPrice: 0,
                                            detailDiscount: 0,
                                            serveStatus: "Pending",
                                            lastServedDate: new Date().toISOString(),
                                            chargingVisa: "",
                                            costEstimate: 0,
                                            totalReceivedQty: 0,
                                            referencePRDetailsId: item.id
                                        }));

                                        setPoItems(mappedItems);
                                        setPrDetailsModal(false);
                                        setActiveTab("items");

                                        await Swal.fire({
                                            icon: 'success',
                                            title: 'Items Loaded',
                                            text: `${mappedItems.length} items loaded successfully!`,
                                            timer: 2000,
                                            showConfirmButton: false
                                        });

                                    } catch (error) {
                                        console.error("Error:", error);
                                        await Swal.fire({ icon: 'error', title: 'Failed', text: 'Failed to process items.', confirmButtonText: 'OK' });
                                    }
                                }}
                                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
                            >
                                Proceed to Items ({Object.values(checkedItems).filter(v => v).length} selected)
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === "items" && (
                <div className="max-w-8xl mx-auto space-y-6">
                    <div className="bg-white/70 backdrop-blur-2xl rounded-3xl p-8 border border-white/ shadow-xl">
                        <h3 className="text-3xl font-bold text-gray-900 mb-2">Purchase Order Item Details</h3>
                        <p className="text-gray-600">Add and manage items for this purchase order</p>
                    </div>

                    <div className="bg-white/70 backdrop-blur-2xl rounded-3xl border border-white/60 shadow-xl overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="bg-gradient-to-r from-blue-500/10 via-blue-500/10 to-purple-500/10 backdrop-blur-sm">
                                        <th className="px-6 py-4 text-left text-sm font-bold text-gray-800 uppercase tracking-wider">Ref. Item Code</th>
                                        <th className="px-6 py-4 text-left text-sm font-bold text-gray-800 uppercase tracking-wider">Qty</th>
                                        <th className="px-6 py-4 text-left text-sm font-bold text-gray-800 uppercase tracking-wider">UOM</th>
                                        <th className="px-6 py-4 text-left text-sm font-bold text-gray-800 uppercase tracking-wider">Item Description</th>
                                        <th className="px-6 py-4 text-left text-sm font-bold text-gray-800 uppercase tracking-wider">Specification</th>
                                        <th className="px-6 py-4 text-left text-sm font-bold text-gray-800 uppercase tracking-wider">Unit Price</th>
                                        <th className="px-6 py-4 text-left text-sm font-bold text-gray-800 uppercase tracking-wider">Discount %</th>
                                        <th className="px-6 py-4 text-left text-sm font-bold text-gray-800 uppercase tracking-wider">Total</th>
                                        <th className="px-6 py-4 text-center text-sm font-bold text-gray-800 uppercase tracking-wider">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200/50">
                                    {poItems.map((item, index) => {
                                        const lineTotal = item.qty * item.unitPrice;
                                        const discountAmount = useDiscount ? (lineTotal * item.detailDiscount) / 100 : 0;
                                        const total = lineTotal - discountAmount;

                                        return (
                                            <tr key={index} className="hover:bg-white/50 transition-all duration-200">
                                                <td className="px-6 py-4">
                                                    <input type="text" value={item.itemCode || ""} disabled
                                                        className="w-full px-4 py-2.5 bg-gray-100 border border-gray-200 rounded-xl text-sm cursor-not-allowed text-gray-500"
                                                        placeholder="Item Code" />
                                                </td>
                                                <td className="px-6 py-4">
                                                    <input type="number" value={item.qty || 0} disabled
                                                        className="w-24 px-4 py-2.5 bg-gray-100 border border-gray-200 rounded-xl text-sm cursor-not-allowed text-gray-500" />
                                                </td>
                                                <td className="px-6 py-4">
                                                    <select value={item.uom || ""} disabled
                                                        className="w-28 px-3 py-2.5 bg-gray-100 border border-gray-200 rounded-xl text-sm cursor-not-allowed text-gray-500">
                                                        <option value="">Select UOM</option>
                                                        {uoms.map((u) => (<option key={u.id} value={u.uom}>{u.uom}</option>))}
                                                    </select>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <textarea value={item.itemDescription || ""} disabled
                                                        className="w-full px-4 py-2.5 bg-gray-100 border border-gray-200 rounded-xl text-sm cursor-not-allowed text-gray-500 resize-none"
                                                        rows="2" />
                                                </td>
                                                <td className="px-6 py-4">
                                                    <textarea value={item.specification || ""} disabled
                                                        className="w-full px-4 py-2.5 bg-gray-100 border border-gray-200 rounded-xl text-sm cursor-not-allowed text-gray-500 resize-none"
                                                        rows="2" />
                                                </td>
                                                <td className="px-6 py-4">
                                                    <input type="number" step="0.01" value={item.unitPrice || 0}
                                                        onChange={(e) => updateItem(index, "unitPrice", e.target.value)}
                                                        className="w-32 px-4 py-2.5 bg-white/80 border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-blue-500/50 text-sm" />
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex flex-col gap-1">
                                                        <div className="flex items-center gap-2">
                                                            <input type="number" step="0.01" value={item.detailDiscount || 0}
                                                                onChange={(e) => updateItem(index, "detailDiscount", e.target.value)}
                                                                disabled={!useDiscount}
                                                                className="w-24 px-4 py-2.5 bg-white/80 border border-gray-200/50 rounded-xl disabled:bg-gray-100 disabled:cursor-not-allowed focus:ring-2 focus:ring-blue-500/50 text-sm" />
                                                            <span className="text-sm font-semibold text-gray-600">%</span>
                                                        </div>
                                                        {useDiscount && discountAmount > 0 && (
                                                            <span className="text-xs text-red-500 font-semibold">
                                                                -₱{discountAmount.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className="text-lg font-bold text-gray-900">
                                                        ₱{total.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <button onClick={() => removeItem(index)}
                                                        className="w-10 h-10 bg-gradient-to-r from-red-500 to-rose-600 text-white rounded-xl hover:from-red-600 hover:to-rose-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-110 font-bold text-lg">
                                                        ×
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>



                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="bg-white/70 backdrop-blur-2xl rounded-3xl p-8 border border-white/60 shadow-xl">
                            <h4 className="text-xl font-bold text-gray-900 mb-6">VAT & Discount Options</h4>
                            <div className="space-y-4">
                                <label className="flex items-center gap-4 cursor-pointer p-4 rounded-xl hover:bg-white/50 transition-all group">
                                    <input type="checkbox" checked={isVatInclusive} onChange={(e) => {
                                        setIsVatInclusive(e.target.checked);
                                        if (e.target.checked) setIsNonVat(false);
                                    }}
                                        className="w-5 h-5 text-blue-600 rounded-lg focus:ring-2 focus:ring-blue-500 transition-all" />

                                    <span className="text-base font-semibold text-gray-800 group-hover:text-blue-600 transition-colors">Is VAT Inclusive?</span>
                                </label>
                                <label className="flex items-center gap-4 cursor-pointer p-4 rounded-xl hover:bg-white/50 transition-all group">
                                    <input type="checkbox" checked={useDiscount} onChange={(e) => setUseDiscount(e.target.checked)}
                                        className="w-5 h-5 text-blue-600 rounded-lg focus:ring-2 focus:ring-blue-500 transition-all" />
                                    <span className="text-base font-semibold text-gray-800 group-hover:text-blue-600 transition-colors">Use Discount?</span>
                                </label>
                                <label className="flex items-center gap-4 cursor-pointer p-4 rounded-xl hover:bg-white/50 transition-all group">
                                    <input type="checkbox" checked={isNonVat} onChange={(e) => {
                                        setIsNonVat(e.target.checked);
                                        if (e.target.checked) setIsVatInclusive(false);
                                    }}
                                        className="w-5 h-5 text-blue-600 rounded-lg focus:ring-2 focus:ring-blue-500 transition-all" />
                                    <span className="text-base font-semibold text-gray-800 group-hover:text-blue-600 transition-colors">Is NON-VAT?</span>
                                </label>
                            </div>
                        </div>

                        <div className="bg-white/70 backdrop-blur-2xl rounded-3xl p-8 border border-white/60 shadow-xl">
                            <h4 className="text-xl font-bold text-gray-900 mb-6">Order Summary</h4>
                            <div className="space-y-4">
                                <div className="flex justify-between items-center p-4 bg-white/50 rounded-xl">
                                    <span className="font-semibold text-gray-700">SubTotal (VAT-Ex):</span>
                                    <span className="font-mono font-bold text-gray-900 text-lg">₱{calculateSubtotal().toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                </div>
                                <div className="flex justify-between items-center p-4 bg-white/50 rounded-xl">
                                    <span className="font-semibold text-gray-700">VAT Amount:</span>
                                    <span className="font-mono font-bold text-gray-900 text-lg">₱{calculateVAT().toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                </div>
                                <div className="flex justify-between items-center p-4 bg-white/50 rounded-xl">
                                    <span className="font-semibold text-gray-700">Disc. Amount:</span>
                                    <span className="font-mono font-bold text-red-500 text-lg">
                                        -₱{calculateTotalDiscount().toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center p-5 bg-gradient-to-r from-blue-500/20 to-blue-500/20 rounded-2xl border-2 border-blue-300/50 mt-6">
                                    <span className="font-bold text-xl text-gray-900">NET AMOUNT</span>
                                    <span className="font-mono font-bold text-2xl text-blue-600">
                                        ₱{calculateTotal().toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showDraftModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto m-4">
                        <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4 flex justify-between items-center">
                            <h3 className="text-xl font-bold text-white">Load Draft</h3>
                            <button onClick={() => setShowDraftModal(false)} className="text-white hover:text-gray-200 text-2xl">✕</button>
                        </div>
                        <div className="p-4">
                            {savedDrafts.length === 0 ? (
                                <p className="text-gray-500 text-center py-8">No saved drafts found.</p>
                            ) : (
                                <div className="space-y-2">
                                    {savedDrafts.map((draft) => (
                                        <div key={draft.id} className="border border-blue-200 rounded-lg p-4 hover:bg-blue-50 transition-all">
                                            <div className="flex justify-between items-start">
                                                <div className="flex-1">
                                                    <p className="font-bold text-blue-700">{draft.formData.poNumber}</p>
                                                    <p className="text-sm text-gray-600">{new Date(draft.timestamp).toLocaleString()}</p>
                                                    <p className="text-xs text-gray-500 mt-1">
                                                        Items: {draft.poItems.length} | Vendor: {draft.formData.vendorId ? "Selected" : "Not selected"}
                                                    </p>
                                                </div>
                                                <div className="flex gap-2">
                                                    <button onClick={() => loadDraft(draft)}
                                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-semibold">
                                                        Load
                                                    </button>
                                                    <button onClick={() => confirmDelete(draft.id)}
                                                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-semibold transition-all">
                                                        Delete
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {deleteConfirmModal && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60]">
                    <div className="bg-white rounded-xl shadow-2xl max-w-md w-full m-4">
                        <div className="bg-gradient-to-r from-red-600 to-red-700 p-4">
                            <h3 className="text-xl font-bold text-white">⚠️ Confirm Delete</h3>
                        </div>
                        <div className="p-6">
                            <p className="text-gray-700 mb-6">Are you sure you want to delete this draft? This action cannot be undone.</p>
                            <div className="flex gap-3 justify-end">
                                <button onClick={() => setDeleteConfirmModal(null)}
                                    className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-all font-semibold">
                                    Cancel
                                </button>
                                <button onClick={handleDeleteConfirm}
                                    className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all font-semibold">
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === "remarks" && (
                <div className="space-y-6">
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            PO Remarks/Special Instructions
                        </label>
                        <textarea
                            className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                            rows={4}
                            placeholder="Enter remarks here..."
                            value={formData.remarks}
                            onChange={(e) => handleInputChange(e, "remarks")}
                        />
                    </div>
                </div>
            )}

            <div className="flex flex-col md:flex-row justify-between items-center gap-4 mt-6 px-4 md:px-8">
                <div className="flex gap-3">
                    <button
                        onClick={() => {
                            if (activeTab === "items") setActiveTab("details");
                            if (activeTab === "remarks") setActiveTab("items");
                        }}
                        disabled={activeTab === "info"}
                        className={`px-6 py-3 rounded-lg font-semibold transition-all ${activeTab === "info" ? "bg-slate-200 text-slate-400 cursor-not-allowed" : "bg-slate-200 text-slate-700 hover:bg-slate-300"}`}
                    >
                        ← Previous
                    </button>
                    <button
                        onClick={() => {
                            if (activeTab === "details") setActiveTab("items");
                            if (activeTab === "items") setActiveTab("remarks");
                            if (activeTab === "remarks") setActiveTab("details");
                        }}
                        disabled={activeTab === "attachments"}
                        className={`px-6 py-3 rounded-lg font-semibold transition-all ${activeTab === "attachments" ? "bg-slate-200 text-slate-400 cursor-not-allowed" : "bg-blue-600 text-white hover:bg-blue-700"}`}
                    >
                        Next →
                    </button>
                </div>

                <div className="text-sm text-slate-600 font-semibold">
                    Step {activeTab === "info" ? "1" : activeTab === "items" ? "2" : "3"} of 3
                </div>

                <div className="flex flex-wrap gap-3">
                    <button onClick={saveDraft}
                        className="px-6 py-3 bg-gray-500 text-white rounded-xl hover:bg-gray-600 transition-all font-semibold shadow-lg">
                        💾 Save Draft
                    </button>
                    <button onClick={() => setShowDraftModal(true)}
                        className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-700 text-white rounded-xl hover:from-purple-700 hover:to-blue-800 transition-all font-semibold shadow-lg">
                        📂 Load Draft
                    </button>
                    <button onClick={handleSubmit}
                        className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all font-semibold shadow-lg">
                        Create Purchase Order
                    </button>
                </div>
            </div>
        </div>
    );
}